import { proxy, useSnapshot } from 'valtio';
import {
  CourseKindSchema,
  type CourseKind,
} from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import {
  WorkSchedule,
  CourseSegment,
  SkillSetContractCategories,
} from '@duke-hq/shared-constants/enrolments';
import type { ConsultantRoleType } from '@duke-hq/svc-api/provider/util/consultants';
import { djs } from '@duke-hq/djs';

export type User = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role?: ConsultantRoleType;
};

export type Consultant = {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: ConsultantRoleType;
  providerId?: string | number | null;
  site?: string;
  region?: string;
};

export type BulkEnrolmentMember = Consultant & {
  isExistingUser?: boolean;
};

export type InternalEnrolmentEnrolmentSettingsType = {
  contract_category: SkillSetContractCategories;
  enrolment_contract_type: string;
  course_segment: CourseSegment;
  external_id: string;
  start_at_date: string; // Must be in YYYY-MM-DD format
  semester_2_start_at_date: string | null; // Must be in YYYY-MM-DD format
  work_schedule: WorkSchedule;
  site_location: {
    site_id: string;
    site_name: string;
    site_state: string;
  };
};

export type CsvError = Record<string, string>;

export enum InternalEnrolmentMode {
  NEW = 'new',
  EXISTING = 'existing',
  BULK = 'bulk',
}

type BulkInternalEnrolmentState = {
  mode: InternalEnrolmentMode;
  setMode: (mode: InternalEnrolmentMode) => void;
  isModalOpen: boolean;
  selectedMembers: BulkEnrolmentMember[];
  setSelectedMembers: (members: BulkEnrolmentMember[]) => void;
  selectedCourseAttributes: {
    id: string;
    identifier: string;
    price_aud: number;
    specialisation: string;
    title: string;
    kind: CourseKind;
  };
  setSelectedCourseAttributes: ({
    id,
    identifier,
    price_aud,
    specialisation,
    title,
    kind,
  }: {
    id: string;
    identifier: string;
    price_aud: number;
    specialisation: string;
    title: string;
    kind: CourseKind;
  }) => void;
  enrolmentSettingsData: InternalEnrolmentEnrolmentSettingsType;
  setEnrolmentSettingsData: ({
    contract_category,
    enrolment_contract_type,
    course_segment,
    external_id,
    start_at_date,
    semester_2_start_at_date,
    work_schedule,
    site_location,
  }: InternalEnrolmentEnrolmentSettingsType) => void;
  csvErrors: CsvError[];
  setCsvErrors: (errors: CsvError[]) => void;
  csvSuccess: BulkEnrolmentMember[];
  setCsvSuccess: (errors: BulkEnrolmentMember[]) => void;
  openModal: () => void;
  closeModal: () => void;
  resetStates: () => void;
};

export const bulkInternalEnrolmentState = proxy<BulkInternalEnrolmentState>({
  mode: InternalEnrolmentMode.EXISTING,
  isModalOpen: false,
  selectedMembers: [] as BulkEnrolmentMember[],
  setSelectedMembers: (members: BulkEnrolmentMember[]) => {
    bulkInternalEnrolmentState.selectedMembers = members;
  },
  selectedCourseAttributes: {
    id: '',
    identifier: '',
    price_aud: 0,
    specialisation: '',
    title: '',
    kind: CourseKindSchema.enum.QUALIFICATION,
  },
  setSelectedCourseAttributes: ({
    id,
    identifier,
    price_aud,
    specialisation,
    title,
    kind,
  }: {
    id: string;
    identifier: string;
    price_aud: number;
    specialisation: string;
    title: string;
    kind: CourseKind;
  }) => {
    bulkInternalEnrolmentState.selectedCourseAttributes = {
      id,
      identifier,
      price_aud,
      specialisation,
      title,
      kind,
    };
  },
  enrolmentSettingsData: {
    contract_category: SkillSetContractCategories.INTERNAL_ENROLMENTS,
    enrolment_contract_type: 'Other',
    course_segment: CourseSegment.FULL_COURSE,
    external_id: '',
    start_at_date: djs().format('YYYY-MM-DD'),
    semester_2_start_at_date: null,
    work_schedule: WorkSchedule.FULL_TIME,
    site_location: { site_id: '', site_name: '', site_state: '' },
  },
  setEnrolmentSettingsData: ({
    contract_category,
    enrolment_contract_type,
    course_segment,
    external_id,
    start_at_date,
    semester_2_start_at_date,
    work_schedule,
    site_location,
  }: InternalEnrolmentEnrolmentSettingsType) => {
    bulkInternalEnrolmentState.enrolmentSettingsData = {
      contract_category,
      enrolment_contract_type,
      course_segment,
      external_id,
      start_at_date,
      semester_2_start_at_date,
      work_schedule,
      site_location,
    };
  },
  csvErrors: [] as CsvError[],
  setCsvErrors: (errors: CsvError[]) => {
    bulkInternalEnrolmentState.csvErrors = errors;
  },
  csvSuccess: [] as BulkEnrolmentMember[],
  setCsvSuccess: (members: BulkEnrolmentMember[]) => {
    bulkInternalEnrolmentState.csvSuccess = members;
  },
  openModal: () => {
    bulkInternalEnrolmentState.isModalOpen = true;
  },
  closeModal: () => {
    bulkInternalEnrolmentState.isModalOpen = false;
    bulkInternalEnrolmentState.resetStates();
  },
  resetStates: () => {
    bulkInternalEnrolmentState.selectedMembers = [];
    bulkInternalEnrolmentState.csvErrors = [];
    bulkInternalEnrolmentState.csvSuccess = [];
    bulkInternalEnrolmentState.selectedCourseAttributes = {
      id: '',
      identifier: '',
      title: '',
      specialisation: '',
      price_aud: 0,
      kind: CourseKindSchema.enum.QUALIFICATION,
    };
    bulkInternalEnrolmentState.enrolmentSettingsData = {
      contract_category: SkillSetContractCategories.INTERNAL_ENROLMENTS,
      enrolment_contract_type: 'Other',
      course_segment: CourseSegment.FULL_COURSE,
      external_id: '',
      start_at_date: djs().format('YYYY-MM-DD'),
      semester_2_start_at_date: null,
      work_schedule: WorkSchedule.FULL_TIME,
      site_location: { site_id: '', site_name: '', site_state: '' },
    };
    bulkInternalEnrolmentState.isModalOpen = false;
  },
  setMode: (mode: InternalEnrolmentMode) => {
    bulkInternalEnrolmentState.mode = mode;
  },
});

export function useBulkInternalEnrolments() {
  const snap = useSnapshot(bulkInternalEnrolmentState);
  return {
    ...snap,
    state: bulkInternalEnrolmentState,
  };
}
